/**
 * Major-aura icon row (#137) — the REUSABLE curated aura-chip strip shared by
 * the world-nameplate overlay (#137, {@link NameplateAuras}) and each frame of
 * the consolidated unit-frame stack (#138, {@link UnitFrameStack}). One filter +
 * sort + render, two hosts.
 *
 * DISTINCT from the self buff/debuff bar (#139, {@link SelfAuraBar}): that shows
 * (nearly) EVERY one of the viewer's OWN auras; this shows only a CURATED MAJOR
 * subset (CC / big defensives / key debuffs — not every tick-DoT) of ANY unit's
 * auras. The subset is selected by aura CATEGORY (see `auraIcons.ts`), driven by
 * prefs at the call site — never hardcoded here.
 *
 * PURELY COSMETIC / CLIENT-SIDE. Reads the already-wired per-entity `auras`
 * (`WireEntity.auras`) — no sim, no protocol, no hash, no SIM_VERSION. Reuses the
 * shared {@link AuraChip} tile (icon + duration + stacks + border tint) and the
 * `auraIcons.ts` category/kind tables.
 *
 * data-* contract (component + e2e assertions):
 *   • data-testid   — `testId` prop (default "major-aura-row")
 *   • data-count    — number of chips shown (post filter + max cap)
 *   • each chip is an <AuraChip> (data-testid = `chipTestId`, default
 *     "major-aura-chip")
 */

import type { CSSProperties, ReactElement } from "react";
import type { WireAura } from "@pit/protocol";
import {
  auraCategory,
  auraCategoryPriority,
  DEFAULT_NAMEPLATE_AURA_CATEGORIES,
  type AuraCategory,
} from "../champions/auraIcons.js";
import { AuraChip, DEFAULT_AURA_CHIP_STYLE, type AuraChipStyle } from "./AuraChip.js";

/** Sim tick rate — matches MatchScreen / SelfAuraBar / Countdown. */
const TICK_RATE = 30;

/** Layout treatment for a major-aura row. Every knob is data-driven (#48). */
export interface MajorAuraRowStyle {
  /** Gap between chips, px. */
  readonly chipGapPx: number;
  /** Per-chip visual style (smaller than the self-bar chip by default). */
  readonly chip: AuraChipStyle;
}

/** Default treatment: compact chips (nameplate/frame scale), tight spacing. */
export const DEFAULT_MAJOR_AURA_ROW_STYLE: MajorAuraRowStyle = {
  chipGapPx: 3,
  chip: { ...DEFAULT_AURA_CHIP_STYLE, sizePx: 24 },
};

/** True when this aura's curation category is in the selected "major" set. */
export function isMajorAura(
  aura: WireAura,
  categories: readonly AuraCategory[],
): boolean {
  return categories.includes(auraCategory(aura.def));
}

/**
 * Total order for a major-aura row: category priority first (CC → defensive →
 * debuff → minor), then soonest-expiring (permanent last), then def id, source
 * seat, applied tick. A TOTAL order, so render order never wobbles between equal
 * auras (mirrors {@link compareAurasForBar} with a category-priority primary
 * key).
 */
export function compareMajorAuras(a: WireAura, b: WireAura, now: number): number {
  const ap = auraCategoryPriority(auraCategory(a.def));
  const bp = auraCategoryPriority(auraCategory(b.def));
  if (ap !== bp) return ap - bp;
  const aPermanent = a.expiresAtTick === null;
  const bPermanent = b.expiresAtTick === null;
  if (aPermanent !== bPermanent) return aPermanent ? 1 : -1;
  if (a.expiresAtTick !== null && b.expiresAtTick !== null) {
    const aRemaining = a.expiresAtTick - now;
    const bRemaining = b.expiresAtTick - now;
    if (aRemaining !== bRemaining) return aRemaining - bRemaining;
  }
  if (a.def !== b.def) return a.def < b.def ? -1 : 1;
  if (a.sourceSeat !== b.sourceSeat) return a.sourceSeat - b.sourceSeat;
  return a.appliedAtTick - b.appliedAtTick;
}

/**
 * Filter `auras` to the major subset (by category selection) and return them in
 * canonical display order. Pure — the reducer of the whole row, exposed so tests
 * drive filter + sort directly. Optional `max` caps the row, keeping the
 * highest-priority N after sorting (a crowded nameplate stays readable).
 */
export function majorAuras(
  auras: readonly WireAura[],
  categories: readonly AuraCategory[],
  now: number,
  max?: number,
): readonly WireAura[] {
  const filtered = auras.filter((a) => isMajorAura(a, categories));
  filtered.sort((x, y) => compareMajorAuras(x, y, now));
  return max === undefined ? filtered : filtered.slice(0, max);
}

export interface MajorAuraRowProps {
  readonly auras: readonly WireAura[];
  /** Current sim tick (for each chip's `expiresAtTick − now` countdown). */
  readonly now: number;
  /** Ticks per second (chip countdown). Defaults to the sim rate. */
  readonly tickRate?: number;
  /** Selected "major" categories. Defaults to the shipped nameplate set. */
  readonly categories?: readonly AuraCategory[];
  /** Cap on chips shown (post-sort). Undefined = uncapped. */
  readonly max?: number;
  readonly style?: MajorAuraRowStyle;
  /** Overridable test id (default "major-aura-row") so hosts can namespace. */
  readonly testId?: string;
  /** Per-chip test id (default "major-aura-chip"). */
  readonly chipTestId?: string;
}

export function MajorAuraRow({
  auras,
  now,
  tickRate = TICK_RATE,
  categories = DEFAULT_NAMEPLATE_AURA_CATEGORIES,
  max,
  style = DEFAULT_MAJOR_AURA_ROW_STYLE,
  testId = "major-aura-row",
  chipTestId = "major-aura-chip",
}: MajorAuraRowProps): ReactElement {
  const shown = majorAuras(auras, categories, now, max);

  const container: CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: style.chipGapPx,
    pointerEvents: "none",
  };

  return (
    <div data-testid={testId} data-count={String(shown.length)} style={container}>
      {shown.map((aura) => (
        <AuraChip
          key={`${aura.def}:${String(aura.sourceSeat)}:${String(aura.appliedAtTick)}`}
          aura={aura}
          now={now}
          tickRate={tickRate}
          style={style.chip}
          testId={chipTestId}
        />
      ))}
    </div>
  );
}
